<script>
    import { mapActions, mapGetters } from 'vuex';
    import { required } from 'vuelidate/lib/validators';
    import InputControl from '@/components/Form/InputControl.vue';
    import AutosizeTextarea from '@/components/Form/AutosizeTextarea.vue';
    import CheckboxGroup from '@/components/CheckboxGroup.vue';
    import InputControlDate from '@/components/Form/InputControlDate.vue';
    import { ISSUE_PERMISSIONS, ISSUE_PRIORITY } from '@/constants';
    import InputError from '@/components/Form/InputError.vue';
    import CloseButton from '@/components/Modals/CloseButton.vue';
    import SelectControl from '@/components/Controls/SelectControl.vue';
    import AddPhotos from '@/components/AddPhotos.vue';
    import PrioritySelector from '@/components/Issues/PrioritySelector.vue';

    export default {
        name: 'RaiseIssueModal',

        components: {
            SelectControl,
            CloseButton,
            InputError,
            InputControlDate,
            InputControl,
            AutosizeTextarea,
            CheckboxGroup,
            AddPhotos,
            PrioritySelector,
        },

        props: {
            questionId: {
                type: String,
                required: true,
            },
        },

        data: () => ({
            isIssueExists: false,
            form: {
                title: '',
                description: '',
                assignees: [],
                priority: ISSUE_PRIORITY.MEDIUM,
                deadline: null,
                suggestedCorrectiveAction: '',
                photos: [],
            },
        }),

        validations: {
            form: {
                title: { required },
            },
        },

        mounted() {
            if (!this.issueForInspection) return;

            const {
                title,
                description,
                priority,
                deadline,
                // eslint-disable-next-line camelcase
                suggested_corrective_action,
                assignees,
                photos,
            } = this.inspection.issues[this.issueForInspection].params;

            this.form = {
                title,
                description,
                assignees: assignees.users || [],
                priority,
                deadline,
                suggestedCorrectiveAction: suggested_corrective_action,
                photos,
            };
        },

        methods: {
            ...mapActions({
                issueSaved: 'conduct/issueSaved',
                issueRemoved: 'conduct/issueRemoved',
            }),

            handleClose() {
                this.$emit('close');
            },

            saveButtonClicked() {
                this.$v.$touch();

                if (this.$v.$invalid) {
                    this.$toasted.show(
                        this.$t('This page has form errors, please fix them'),
                        { type: 'error' },
                    );
                } else {
                    this.issueSaved({
                        questionId: this.questionId,
                        form: this.form,
                    });
                    this.handleClose();
                }
            },

            removeButtonClicked() {
                this.issueRemoved(this.questionId);
                this.handleClose();
            },

            assigneesChanged(assignees) {
                this.form.assignees = assignees;
            },
        },

        computed: {
            ...mapGetters({
                hasPermission: 'authentication/hasPermission',
                inspection: 'conduct/inspection',
                assignees: 'assignees',
            }),

            canCreateIssueWithAssignee() {
                return this.hasPermission(
                    ISSUE_PERMISSIONS.CREATE_ISSUE_WITH_ASSIGNEE,
                );
            },

            issueForInspection() {
                return (
                    this.inspection.issues
                    && Object.keys(this.inspection.issues).find(
                        (uuid) => uuid === this.questionId,
                    )
                );
            },

            assigneesOptions() {
                return Object.keys(this.assignees).map((id) => ({
                    value: id,
                    label: this.assignees[id],
                }));
            },
        },
    };
</script>

<template>
    <modal class="overflow-scroll">
        <div class="modal-wrapper">
            <CloseButton @click.native="handleClose" />

            <div
                class="modal-content modal-content--scrollable"
                :class="{
                    'modal-content--editting': issueForInspection
                }"
            >
                <InputControl :label="$t('Title')">
                    <input
                        class="form-input form-control form-input-bordered w-full"
                        id="title"
                        v-model.trim.lazy="$v.form.title.$model"
                        name="title"
                        type="text"
                        :placeholder="$t('Enter title')"
                    />
                    <div v-if="$v.form.title.$anyError">
                        <InputError
                            v-if="$v.form.title.$dirty"
                            :condition="!$v.form.title.required"
                        >
                            {{
                            $t('The {field} field is required', {
                            field: $t('title'),
                            })
                            }}
                        </InputError>
                    </div>
                </InputControl>

                <InputControl :label="$t('Description (optional)')">
                    <AutosizeTextarea
                        v-model.trim.lazy="form.description"
                        :placeholder="$t('Enter description')"
                    />
                </InputControl>

                <InputControl>
                    <AddPhotos v-model="form.photos" />
                </InputControl>

                <InputControl
                    v-if="canCreateIssueWithAssignee"
                    :label="$t('Assignee (optional)')"
                >
                    <SelectControl
                        class="w-full"
                        :multiple="true"
                        :options="assigneesOptions"
                        :value="form.assignees"
                        :not-selected-label="$t('Select assignees')"
                        @input="assigneesChanged(arguments[0])"
                    />
                </InputControl>

                <InputControl :label="$t('Priority')">
                    <PrioritySelector
                        v-model="form.priority"
                    />
                </InputControl>

                <InputControl :label="$t('Due at (optional)')">
                    <InputControlDate
                        v-model="form.deadline"
                        :class="{
                            empty: form.deadline === null
                        }"
                        :placeholder="$t('Select deadline')"
                        display-time
                        wrap-size
                    />
                </InputControl>

                <InputControl :label="$t('Suggested corrective action (optional)')">
                    <AutosizeTextarea
                        v-model.trim.lazy="form.suggestedCorrectiveAction"
                        class="min-h-textarea"
                        :placeholder="
                            $t('Enter sugested corrective action')
                        "
                    />
                </InputControl>

                <p class="mb-4">
                    {{ $t('Click done to raise issue when submitting your check results') }}
                </p>
            </div>

            <div class="modal-actions-outer-wrapper">
                <div class="modal-actions-inner-wrapper">
                    <button
                        class="modal-actions-secondary modal-actions-secondary--transparent"
                        type="button"
                        v-if="issueForInspection"
                        @click="removeButtonClicked"
                    >
                        <span>{{ $t('Remove') }}</span>
                    </button>
                    <button
                        class="modal-actions-primary"
                        type="button"
                        @click="saveButtonClicked"
                    >
                        <span>{{ $t('Done') }}</span>
                    </button>
                </div>
            </div>
        </div>
    </modal>
</template>
